import { createSlice } from '@reduxjs/toolkit';
import { parseJwt } from '../../utils/jwt';

const extractUserObjects = (userObjects) => {
  const userObjs = {
    assignedCountries: [],
    assignedBusinessLines: [],
    assignedLocations: [],
    assignedRegions: [],
  };

  if (!Array.isArray(userObjects) || userObjects.length === 0) {
    return userObjs;
  }

  const getConfigData = (objectType, valueNumber) => {
    const configData = userObjects.find((x) => x.ObjectType === objectType);
    if (configData && configData.ObjectData) {
      const arr = JSON.parse(configData.ObjectData);
      if (Array.isArray(arr)) {
        if (valueNumber) {
          return arr.map((x) => +x.ID);
        }

        return arr.map((x) => x.ID);
      }

      return [];
    }

    return null;
  };

  userObjs.assignedCountries = getConfigData('Country') || [];
  userObjs.assignedBusinessLines = getConfigData('BusinessLine') || [];
  userObjs.assignedLocations = getConfigData('Location', true) || [];
  userObjs.assignedRegions = getConfigData('Region') || [];
  return userObjs;
};

const parseRoleClaims = (roleClaims) => {
  const emptySet = {};
  try {
    if (!Array.isArray(roleClaims) || roleClaims.length === 0) {
      return emptySet;
    }

    const permissionClaim = roleClaims.find((c) => c.ClaimName === 'Permission');
    if (!permissionClaim) {
      return emptySet;
    }

    const permissionCodes = JSON.parse(permissionClaim.ClaimValue) || [];
    return permissionCodes.reduce((previousValue, currentValue) => {
      if (currentValue) {
        previousValue[currentValue.toLowerCase()] = true;
      }

      return previousValue;
    }, {});
  } catch (error) {
    console.log(error);
  }

  return emptySet;
};

const transformUserFunctionality = (userFunctionalities) => {
  const emptySet = {};
  try {
    if (!Array.isArray(userFunctionalities) || userFunctionalities.length === 0) {
      return emptySet;
    }

    return userFunctionalities.reduce((prev, { Code, Options }) => {
      if (prev) {
        prev[Code.toLowerCase()] = Options;
      }

      return prev;
    }, {});
  } catch (error) {
    console.log(error);
  }

  return emptySet;
};

const initialState = {
  isAuthenticated: false,
  loginUser: null,
  impersonatedUser: null,
  isValidatingToken: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // updateAuthData(state, action) {
    //   Object.keys(initialState).forEach((key) => {
    //     state[key] = action.payload[key];
    //   });
    // },

    updateLoginUser(state, action) {
      const {
        isAuthenticated,
        user,
        token,
        userObjects,
        dateFormat,
        dateTimeFormat,
        tenantList,
        permissions,
        customPermissions,
      } = action.payload;

      const {
        assignedCountries,
        assignedBusinessLines,
        assignedLocations,
        assignedRegions,
      } = extractUserObjects(userObjects);

      const { uname: username, tenantId } = parseJwt(token) || {};

      state.isAuthenticated = isAuthenticated;
      const permissionSet = transformUserFunctionality(permissions);

      state.loginUser = {
        user,
        username,
        assignedCountries,
        assignedBusinessLines,
        assignedLocations,
        assignedRegions,
        dateFormat,
        dateTimeFormat,
        permissionSet,
        tenantId,
        tenantList,
        customPermissions,
      };
    },
    logout(state) {
      state.isAuthenticated = false;
      state.loginUser = null;
    },
    startImpersonating(state, action) {
      const {
        UserAccountID,
        UserID,
        Username,
        dateFormat,
        dateTimeFormat,
        user,
        roleClaims,
        userObjects,
        tenantList,
      } = action.payload;

      const {
        assignedCountries: assignedDivisions,
        assignedBusinessLines: assignedClients,
        assignedLocations,
        assignedSourceSystems,
        assignedRegions: assignedProjects,
      } = extractUserObjects(userObjects);

      const permissionSet = parseRoleClaims(roleClaims);

      state.impersonatedUser = {
        UserAccountID,
        UserID,
        Username,
        dateFormat,
        dateTimeFormat,
        user,
        assignedDivisions,
        assignedClients,
        assignedLocations,
        assignedSourceSystems,
        assignedProjects,
        permissionSet,
        tenantList,
      };

      state.isImpersonateing = true;
    },
    stopImpersonating(state) {
      state.isImpersonateing = false;
      state.impersonatedUser = null;
    },
    updateAccessToken(state, action) {
      const { tenantId } = parseJwt(action.payload) || {};
      state.loginUser.tenantId = tenantId;
    },
    updateProfileInfo(state, action) {
      const { user } = action.payload;
      state.loginUser.user = user;
      state.loginUser.dateFormat = user.DateFormat;
      state.loginUser.dateTimeFormat = user.DateTimeFormat;
    },

    updateValidatingToken(state, action) {
      state.isValidatingToken = action.payload;
    },
  },
});

export const authActions = authSlice.actions;
export default authSlice.reducer;
